import { type ReactNode } from 'react'
import { type ICardFilter, Typography } from 'shared/ui'
import { numberFormat } from 'shared/lib'
import { type ITooltipPlacementProps } from 'shared/ui/Tooltip/ui/Tooltip'
import { Base } from 'models'
import type { IContactsStatisticFilter } from 'entities/ContactStatistic'
import type { IEntitiesStore } from 'entities/EntitiesExport'

type IStatisticProps = {
  no_replies_count?: number | null
  reply_count?: number | null
  success_count?: number | null
  total_count: number | null
  clicks_count?: number | null
  contacts_count?: number
  conversions_count?: number | null
  revenue_cost?: string | number | null
  undelivered_count?: number | null
  unsubscribed_count?: number
  is_track_conversion?: boolean
  active_count?: number
  quiet_hours_count?: number
}

interface IFilterConfig {
  title?: string
  tooltipLabel?: ReactNode
  tooltipPlacement?: ITooltipPlacementProps['placement']
}

interface IFilterConfigWithTitle extends IFilterConfig {
  title: string
}

export class Statistic extends Base {
  count_at: string | null = null
  created_at: string | null = null
  contacts_count: number | null = null
  skipped_count: number | null = null
  skipped_percent: number | null = null
  no_replies_count?: number | null = null
  reply_count?: number | null = null
  success_count?: number | null = null
  total_count: number | null = 0
  clicks_count?: number | null = null
  conversions_count?: number | null
  revenue_cost?: string | number | null
  undelivered_count?: number | null
  unsubscribed_count?: number
  is_track_conversion?: boolean
  active_count?: number
  quiet_hours_count?: number

  constructor({
    contacts_count = 0,
    no_replies_count,
    reply_count,
    success_count,
    total_count = 0,
    clicks_count,
    conversions_count,
    revenue_cost,
    undelivered_count,
    unsubscribed_count,
    is_track_conversion,
    active_count,
    quiet_hours_count,
  }: IStatisticProps) {
    super()
    this.contacts_count = contacts_count
    this.no_replies_count = no_replies_count
    this.reply_count = reply_count
    this.success_count = success_count
    this.total_count = total_count
    this.skipped_count = contacts_count - (this.total_count || 0)
    this.skipped_percent = Math.round((this.skipped_count / contacts_count) * 100)
    this.clicks_count = clicks_count
    this.conversions_count = conversions_count
    this.revenue_cost = revenue_cost
    this.undelivered_count = undelivered_count
    this.unsubscribed_count = unsubscribed_count
    this.is_track_conversion = is_track_conversion
    this.active_count = active_count
    this.quiet_hours_count = quiet_hours_count
  }

  get success_percent() {
    if (this.success_count && this.total_count) {
      return Math.round((this.success_count / this.total_count) * 100)
    }
    return 0
  }
  get no_replies_percent() {
    if (this.no_replies_count && this.total_count) {
      return Math.round((this.no_replies_count / this.total_count) * 100)
    }
    return 0
  }
  get reply_percent() {
    if (this.reply_count && this.total_count) {
      return Math.round((this.reply_count / this.total_count) * 100)
    }
    return 0
  }
  get clicks_percent() {
    if (this.clicks_count && this.total_count) {
      return Math.round((this.clicks_count / this.total_count) * 100)
    }
    return 0
  }
  get conversions_percent() {
    if (this.conversions_count && this.total_count) {
      return Math.round((this.conversions_count / this.total_count) * 100)
    }
    return 0
  }
  get undelivered_percent() {
    if (this.undelivered_count && this.total_count) {
      return Math.round((this.undelivered_count / this.total_count) * 100)
    }
    return 0
  }
  get unsubscribed_percent() {
    if (this.unsubscribed_count && this.total_count) {
      return Math.round((this.unsubscribed_count / this.total_count) * 100)
    }
    return 0
  }
  get active_percent() {
    if (this.active_count && this.total_count) {
      return Math.round((this.active_count / this.total_count) * 100)
    }
    return 0
  }

  get quiet_hours_percent() {
    if (this.quiet_hours_count && this.total_count) {
      return Math.round((this.quiet_hours_count / this.total_count) * 100)
    }
    return 0
  }

  public getFilters = (
    customConfigs?: Record<string, IFilterConfig>,
    entities?: IEntitiesStore
  ): ICardFilter<IContactsStatisticFilter>[] => {
    const defaultConfigs: Record<string, IFilterConfigWithTitle> = {
      sent_to: {
        title: 'Total',
        tooltipLabel: 'Contacts',
      },
      quiet_hours: {
        title: 'Quiet hours',
        tooltipLabel: ['Contacts whose messages', <br key={'br'} />, 'are delayed by quiet hours'],
      },
      success: {
        title: 'Delivered',
        tooltipLabel: ['Contacts who received', <br key={'br'} />, 'the message'],
      },
      skipped: {
        title: 'Skipped',
        tooltipLabel: [
          'This was canceled during sending',
          <br key={'br'} />,
          <Typography
            ariaLabel={'desc'}
            key={'desc'}
            tag={'div'}
            variant={'body-sm-regular'}
            color={'var(--content-primary-tertiary-inverted)'}
            marginTop={2}
          >
            Before canceling, it was sent to{' '}
            {numberFormat({
              value: this.total_count || 0,
            })}{' '}
            of <br />
            {numberFormat({
              value: this.contacts_count || 0,
            })}{' '}
            eligible contacts.
          </Typography>,
        ],
      },
      replied: {
        title: 'Replies',
        tooltipLabel: ['Contacts who replied', <br key={'br'} />, 'to the message'],
      },
      no_replies: {
        title: 'No Replies',
        tooltipLabel: ['Contacts who didn’t reply', <br key={'br'} />, 'to the message'],
      },
      clicked: {
        title: 'Clicks',
        tooltipLabel: ['Contacts who clicked', <br key={'br'} />, 'on the link(s)'],
      },
      conversions: {
        title: 'Conversions',
        tooltipLabel: 'Contacts who converted',
      },
      revenue: {
        title: 'Revenue',
        tooltipLabel: ['Revenue generated by the', <br key={'br'} />, 'contacts who converted'],
      },
      failed: {
        title: 'Undelivered',
        tooltipLabel: ['Contacts who didnn’t', <br key={'br'} />, 'received the message'],
      },
      subscribed: {
        title: 'Active',
        tooltipLabel: ['Active contacts'],
      },
      opted_out: {
        title: 'Unsubscribed',
        tooltipLabel: 'Contacts who opted-out',
      },
    }

    const configs = Object.keys(defaultConfigs).reduce((acc, key) => {
      acc[key] = {
        ...defaultConfigs[key],
        ...(customConfigs?.[key] || {}),
      }
      return acc
    }, {} as Record<string, IFilterConfigWithTitle>)

    const quietHoursFilter: ICardFilter<IContactsStatisticFilter> | null =
      entities?.featureFlagsStore.quiet_hours &&
      entities.organizationStore.quietHoursSettings?.is_active
        ? {
            key: 'quiet_hours',
            title: configs.quiet_hours.title,
            amount: this.quiet_hours_count ?? 0,
            percentage: this.quiet_hours_percent,
            tooltipProps: {
              label: configs.quiet_hours.tooltipLabel,
              placement: configs.quiet_hours.tooltipPlacement || 'top',
            },
          }
        : null

    return [
      {
        key: 'sent_to',
        title: configs.sent_to.title,
        amount: this.total_count,
        tooltipProps: {
          label: configs.sent_to.tooltipLabel,
          placement: configs.sent_to.tooltipPlacement || 'top',
        },
      },
      ...(quietHoursFilter ? [quietHoursFilter] : []),
      {
        key: 'success',
        title: configs.success.title,
        amount: this.success_count,
        percentage: this.success_percent,
        tooltipProps: {
          label: configs.success.tooltipLabel,
          placement: configs.success.tooltipPlacement || 'top',
        },
      },
      {
        key: 'skipped',
        title: configs.skipped.title,
        amount: this.skipped_count,
        percentage: this.skipped_percent,
        tooltipProps: {
          label: configs.skipped.tooltipLabel,
          placement: configs.skipped.tooltipPlacement || 'top',
        },
      },
      {
        key: 'replied',
        title: configs.replied.title,
        amount: this.reply_count,
        percentage: this.reply_percent,
        tooltipProps: {
          label: configs.replied.tooltipLabel,
          placement: configs.replied.tooltipPlacement || 'top',
        },
      },
      {
        key: 'no_replies',
        title: configs.no_replies.title,
        amount: this.no_replies_count,
        percentage: this.no_replies_percent,
        tooltipProps: {
          label: configs.no_replies.tooltipLabel,
          placement: configs.no_replies.tooltipPlacement || 'top',
        },
      },
      {
        key: 'clicked',
        title: configs.clicked.title,
        amount: this.clicks_count,
        percentage: this.clicks_percent,
        tooltipProps: {
          label: configs.clicked.tooltipLabel,
          placement: configs.clicked.tooltipPlacement || 'top',
        },
      },
      {
        key: 'conversions',
        title: configs.conversions.title,
        amount: this.conversions_count,
        percentage: this.conversions_percent,
        tooltipProps: {
          label: configs.conversions.tooltipLabel,
          placement: configs.conversions.tooltipPlacement || 'top',
        },
      },
      {
        key: 'revenue',
        title: configs.revenue.title,
        amountContent: this.revenue_cost,
        tooltipProps: {
          label: configs.revenue.tooltipLabel,
          placement: configs.revenue.tooltipPlacement || 'top',
        },
      },
      {
        key: 'failed',
        title: configs.failed.title,
        amount: this.undelivered_count,
        percentage: this.undelivered_percent,
        tooltipProps: {
          label: configs.failed.tooltipLabel,
          placement: configs.failed.tooltipPlacement || 'top',
        },
      },
      {
        key: 'subscribed',
        title: configs.subscribed.title,
        amount: this.active_count,
        percentage: this.active_percent,
        tooltipProps: {
          label: configs.subscribed.tooltipLabel,
          placement: configs.subscribed.tooltipPlacement || 'top',
        },
      },
      {
        key: 'opted_out',
        title: configs.opted_out.title,
        amount: this.unsubscribed_count,
        percentage: this.unsubscribed_percent,
        tooltipProps: {
          label: configs.opted_out.tooltipLabel,
          placement: configs.opted_out.tooltipPlacement || 'top',
        },
      },
    ]
  }
}
